import {
  DialogContent,
  Dialog,
  DialogProps,
  DialogActions,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { styled } from '@mui/styles';
import { useFormik } from 'formik';
import PmiTextField from 'shell/components/shared/text-field';
import * as yup from 'yup';
import { DatePicker } from '@mui/lab';
import { DateTime } from 'luxon';

export const modifyHistorianModalValidationSchema = yup.object({
  name: yup.string().nullable().required('Il campo è obbligatorio'),
  type: yup.string().nullable().required('Il campo è obbligatorio'),
  institutionDate: yup
    .date()
    .nullable()
    .required('Il campo è obbligatorio')
    .typeError('Data non valida'),
});

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

interface ModalInsertCohabitationDataProps {
  modalData: {
    name: string;
    type: string;
    institutionDate: DateTime | null;
    closeDate: DateTime | null;
    fiscalCode: string;
    phone1: string;
    phone2: string;
    phone3: string;
  };
  open: boolean;
  onClose: (open: boolean) => void;
  onSubmit: (values: {
    name: string;
    type: string;
    institutionDate: DateTime | null;
    closeDate: DateTime | null;
    fiscalCode: string;
    phone1: string;
    phone2: string;
    phone3: string;
  }) => void;
}

const ModalInsertCohabitationData = ({
  modalData,
  open,
  onClose,
  onSubmit,
}: ModalInsertCohabitationDataProps): React.ReactElement => {
  const form = useFormik({
    initialValues: modalData,
    validationSchema: modifyHistorianModalValidationSchema,
    onSubmit,
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <PmiDialog
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen={fullScreen}
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        Modifica Storico Dati Convivenza
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <PmiTextField
              id="name"
              label="Nome Convivenza *"
              aria-label="Nome Convivenza"
              fullWidth
              value={form.values?.name || ''}
              helperText={form.errors.name}
              error={form.touched.name && Boolean(form.errors.name)}
              onChange={(event) => form.setFieldValue('name', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="type"
              label="Tipo Convivenza *"
              aria-label="Tipo Convivenza"
              fullWidth
              helperText={form.errors.type}
              error={form.touched.type && Boolean(form.errors.type)}
              value={form.values?.type || ''}
              onChange={(event) => form.setFieldValue('type', event.target.value)}
            />
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data istituzione *"
                aria-label="Data istituzione"
                value={form.values?.institutionDate || null}
                onChange={(value) => form.setFieldValue('institutionDate', value)}
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="institutionDate"
                    helperText={form.errors.institutionDate}
                    error={form.touched.institutionDate && Boolean(form.errors.institutionDate)}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data chiusura"
                aria-label="Data chiusura"
                value={form.values?.closeDate || null}
                onChange={(value) => form.setFieldValue('closeDate', value)}
                renderInput={(params) => (
                  <PmiTextField {...params} id="closeDate" helperText="" error={false} fullWidth />
                )}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <PmiTextField
                id="phone1"
                label="Telefono"
                aria-label="Telefono"
                fullWidth
                value={form.values?.phone1 || ''}
                onChange={(event) => form.setFieldValue('phone1', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                id="phone2"
                fullWidth
                value={form.values?.phone2 || ''}
                onChange={(event) => form.setFieldValue('phone2', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                id="phone3"
                fullWidth
                value={form.values?.phone3 || ''}
                onChange={(event) => form.setFieldValue('phone3', event.target.value)}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="fiscalCode"
              label="Codice Fiscale"
              aria-label="Codice Fiscale"
              fullWidth
              value={form.values?.fiscalCode || ''}
              onChange={(event) => form.setFieldValue('fiscalCode', event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <PmiButton
          variant="contained"
          color="primary"
          type="submit"
          onClick={() => {
            form.handleSubmit();
          }}
        >
          CONFERMA
        </PmiButton>
        <PmiButton variant="outlined" color="cdmBrand" type="button" onClick={() => onClose(false)}>
          ESCI
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ModalInsertCohabitationData.displayName = 'Modal insert cohabitation data';

export default ModalInsertCohabitationData;
